import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader, FileText, Link2, Unlink, PenLine, ClipboardList,
  LogIn, KeyRound, UserPlus, UserCheck, UserCog, UserX, MailPlus,
  Database, RefreshCw, Plug, Pencil, Eye, Download,
  MessageSquareText,
  Wand, Filter, Check, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTime } from '../lib/text';

interface AuditEntry {
  id: string;
  action: string;
  user_name: string;
  section_id: string | null;
  section_name?: string | null;
  detail: Record<string, any> | null;
  created_at: string;
}

interface AuditResponse {
  entries: AuditEntry[];
  available_actions: string[];
}

type ActionConfig = { label: string; Icon: typeof FileText; color: string; bg: string };

// Keys are require_auth's "{METHOD} {path_template}", or a stable string from an anonymous-flow
// handler; unlisted keys render as "method path".
const actionConfig: Record<string, ActionConfig> = {
  user_login: { label: 'Logged in', Icon: LogIn, color: 'text-coolslate', bg: 'bg-coolslate/15' },
  password_reset_completed: { label: 'Reset password', Icon: KeyRound, color: 'text-amber-700', bg: 'bg-amber-50' },
  invite_accepted: { label: 'Accepted invite', Icon: UserCheck, color: 'text-state-success', bg: 'bg-state-success/15' },

  'POST /users': { label: 'Invited user', Icon: UserPlus, color: 'text-brand', bg: 'bg-brand/15' },
  'POST /users/{user_id}/resend-invite': { label: 'Resent invite', Icon: MailPlus, color: 'text-brand', bg: 'bg-brand/15' },
  'PUT /users/{user_id}/roles': { label: 'Updated user roles', Icon: UserCog, color: 'text-brand', bg: 'bg-brand/15' },
  'DELETE /users/{user_id}': { label: 'Deleted user', Icon: UserX, color: 'text-state-error', bg: 'bg-state-error/15' },
  'PUT /auth/me': { label: 'Updated profile', Icon: Pencil, color: 'text-brand', bg: 'bg-brand/15' },

  'POST /workspace/links/{definition_id}/{file_id}': { label: 'Linked file', Icon: Link2, color: 'text-state-success', bg: 'bg-state-success/15' },
  'DELETE /workspace/links/{definition_id}/{file_id}': { label: 'Unlinked file', Icon: Unlink, color: 'text-state-error', bg: 'bg-state-error/15' },
  folder_linked: { label: 'Linked folder', Icon: Link2, color: 'text-state-success', bg: 'bg-state-success/15' },
  folder_unlinked: { label: 'Unlinked folder', Icon: Unlink, color: 'text-state-error', bg: 'bg-state-error/15' },
  'DELETE /workspace/folder-links/{definition_id}': { label: 'Unlinked folder', Icon: Unlink, color: 'text-state-error', bg: 'bg-state-error/15' },
  'POST /workspace/types/{type_id}/upload': { label: 'Uploaded file', Icon: FileText, color: 'text-brand', bg: 'bg-brand/15' },
  'POST /workspace/generate/{definition_id}': { label: 'Queued generation', Icon: Wand, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  'GET /files/{file_id}': { label: 'Viewed file', Icon: Eye, color: 'text-coolslate', bg: 'bg-coolslate/15' },
  'POST /files/{file_id}/reimport': { label: 'Re-imported file', Icon: Download, color: 'text-brand', bg: 'bg-brand/15' },

  'PUT /workspace/fields/{field_key}': { label: 'Updated field', Icon: PenLine, color: 'text-brand', bg: 'bg-brand/15' },

  'POST /sources': { label: 'Added connection', Icon: Database, color: 'text-state-success', bg: 'bg-state-success/15' },
  'PUT /sources/{source_id}': { label: 'Updated connection', Icon: Pencil, color: 'text-brand', bg: 'bg-brand/15' },
  'POST /sources/{source_id}/refresh': { label: 'Refreshed connection', Icon: RefreshCw, color: 'text-brand', bg: 'bg-brand/15' },
  'POST /sources/{source_id}/reconnect': { label: 'Reconnected connection', Icon: Plug, color: 'text-brand', bg: 'bg-brand/15' },

  'POST /query': { label: 'Ran query', Icon: MessageSquareText, color: 'text-brand', bg: 'bg-brand/15' },
  'GET /workspace/section/{section_id}': { label: 'Viewed section', Icon: Eye, color: 'text-coolslate', bg: 'bg-coolslate/15' },
  'GET /workspace/types/{type_id}': { label: 'Viewed document type', Icon: Eye, color: 'text-coolslate', bg: 'bg-coolslate/15' },
  'GET /workspace/index': { label: 'Viewed document index', Icon: Eye, color: 'text-coolslate', bg: 'bg-coolslate/15' },
};

function genericLabelFor(action: string): string {
  const m = action.match(/^(GET|POST|PUT|DELETE|PATCH)\s+(.+)$/);
  if (!m) return action;
  const [, method, path] = m;
  const readable = path
    .split('/')
    .filter(Boolean)
    .filter(seg => !seg.startsWith('{'))
    .join(' / ');
  return `${method} ${readable}`;
}

function configFor(action: string): ActionConfig {
  if (actionConfig[action]) return actionConfig[action];
  const method = action.split(' ', 1)[0];
  const Icon = method === 'DELETE' ? UserX
    : method === 'POST' ? PenLine
    : method === 'PUT' ? Pencil
    : Eye;
  const color = method === 'DELETE' ? 'text-state-error' : 'text-coolslate';
  const bg = method === 'DELETE' ? 'bg-state-error/15' : 'bg-coolslate/15';
  return { label: genericLabelFor(action), Icon, color, bg };
}

function pathParamsFromAction(action: string): string[] {
  const m = action.match(/^(?:GET|POST|PUT|DELETE|PATCH)\s+(.+)$/);
  if (!m) return [];
  return Array.from(m[1].matchAll(/\{([^}]+)\}/g)).map(x => x[1]);
}

function ActionDetail({ entry }: { entry: AuditEntry }) {
  const detail = entry.detail;
  if (!detail) return null;

  if (entry.action === 'user_login' || entry.action === 'password_reset_completed') {
    return detail.email ? (
      <span className="text-sm text-coolslate">{detail.email}</span>
    ) : null;
  }
  if (entry.action === 'invite_accepted') {
    return detail.email ? (
      <span className="text-sm text-coolslate">{detail.email}</span>
    ) : null;
  }

  const params = pathParamsFromAction(entry.action);
  const fileId = detail.file_id;
  const sourceId = detail.source_id;
  const fieldKey = detail.field_key;
  const typeId = detail.type_id || detail.definition_id;
  const userId = detail.user_id;

  const pieces: React.ReactNode[] = [];
  if (params.includes('file_id') && fileId) {
    pieces.push(
      <Link key="file" to={`/files/${fileId}`} className="text-brand hover:text-brand-dark transition-colors font-mono text-xs">
        {String(fileId).slice(0, 8)}…
      </Link>
    );
  }
  if (params.includes('source_id') && sourceId) {
    pieces.push(
      <Link key="source" to={`/connections/${sourceId}`} className="text-brand hover:text-brand-dark transition-colors font-mono text-xs">
        {String(sourceId).slice(0, 8)}…
      </Link>
    );
  }
  if (params.includes('field_key') && fieldKey) {
    pieces.push(<span key="field" className="font-mono text-xs text-graphite">{fieldKey}</span>);
  }
  if (params.includes('type_id') && typeId) {
    pieces.push(
      <Link key="type" to={`/types/${typeId}`} className="text-brand hover:text-brand-dark transition-colors font-mono text-xs">
        {typeId}
      </Link>
    );
  }
  if (params.includes('definition_id') && detail.definition_id && !typeId) {
    pieces.push(
      <Link key="def" to={`/types/${detail.definition_id}`} className="text-brand hover:text-brand-dark transition-colors font-mono text-xs">
        {detail.definition_id}
      </Link>
    );
  }
  if (params.includes('user_id') && userId) {
    pieces.push(<span key="user" className="font-mono text-xs text-graphite">{String(userId).slice(0, 8)}…</span>);
  }

  if (pieces.length === 0) {
    if (detail.query && typeof detail.query === 'object') {
      const qs = Object.entries(detail.query).map(([k, v]) => `${k}=${v}`).join(' · ');
      if (qs) return <span className="text-xs text-coolslate font-mono">{qs}</span>;
    }
    return null;
  }
  return (
    <span className="text-sm text-coolslate flex items-center gap-2 flex-wrap">
      {pieces.flatMap((p, i) => i === 0 ? [p] : [<span key={`sep-${i}`} className="text-coolslate/50">·</span>, p])}
    </span>
  );
}

function ActionFilter({
  available, selected, onChange,
}: {
  available: string[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest?.('[data-audit-filter]')) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggle = (action: string) => {
    const next = new Set(selected);
    if (next.has(action)) next.delete(action);
    else next.add(action);
    onChange(next);
  };

  const sortedActions = [...available].sort((a, b) =>
    configFor(a).label.localeCompare(configFor(b).label)
  );

  const summary = selected.size === 0
    ? 'All actions'
    : selected.size === 1
      ? configFor([...selected][0]).label
      : `${selected.size} actions`;

  return (
    <div className="relative" data-audit-filter>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-2 px-3.5 py-2 border border-softgrey rounded-lg bg-white text-sm text-graphite hover:border-brand/40 transition-colors cursor-pointer"
      >
        <Filter className="w-3.5 h-3.5 text-coolslate" />
        <span>{summary}</span>
        <ChevronDown className="w-3.5 h-3.5 text-coolslate" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1.5 w-80 max-h-96 overflow-y-auto navaris-dropdown">
          <button
            type="button"
            onClick={() => onChange(new Set())}
            className="w-full px-3 py-2 text-left text-[13px] text-coolslate hover:bg-offwhite cursor-pointer flex items-center justify-between"
          >
            <span>All actions</span>
            {selected.size === 0 && <Check className="w-3.5 h-3.5 text-brand" />}
          </button>
          <div className="my-1 border-t border-softgrey" />
          {sortedActions.map(action => {
            const cfg = configFor(action);
            const isOn = selected.has(action);
            return (
              <button
                key={action}
                type="button"
                onClick={() => toggle(action)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-offwhite cursor-pointer flex items-center gap-2.5"
              >
                <cfg.Icon className={`w-3.5 h-3.5 ${cfg.color}`} />
                <span className="flex-1 text-graphite truncate">{cfg.label}</span>
                {isOn && <Check className="w-3.5 h-3.5 text-brand" />}
              </button>
            );
          })}
          {sortedActions.length === 0 && (
            <p className="px-3 py-3 text-[13px] text-coolslate text-center">No actions yet</p>
          )}
        </div>
      )}
    </div>
  );
}

export default function ActivityPage() {
  const { authFetch } = useAuth();
  const [data, setData] = useState<AuditResponse | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const params = new URLSearchParams();
    if (selected.size > 0) params.set('actions', [...selected].join(','));
    const qs = params.toString();
    authFetch(`/workspace/audit${qs ? `?${qs}` : ''}`)
      .then(r => r.ok ? r.json() : null)
      .then(payload => {
        if (Array.isArray(payload)) {
          setData({ entries: payload, available_actions: [] });
        } else if (payload) {
          setData(payload);
        } else {
          setData({ entries: [], available_actions: [] });
        }
      })
      .catch(() => setData({ entries: [], available_actions: [] }));
  }, [selected]);

  return (
    <div>
      <div className="pb-6 mb-6 border-b border-softgrey">
        <h3 className="text-lg font-semibold">Activity</h3>
        <p className="text-sm text-coolslate mt-1">A record of user and system actions.</p>
      </div>

      {data === null ? (
        <div className="flex flex-col justify-center items-center h-64">
          <Loader className="w-12 h-12 animate-spin text-brand mb-4" />
          <p className="text-coolslate">Loading activity...</p>
        </div>
      ) : (
        <>
          <div className="mb-4 flex items-center justify-end">
            <ActionFilter available={data.available_actions} selected={selected} onChange={setSelected} />
          </div>

          {data.entries.length === 0 ? (
            <div className="py-12 text-center">
              <ClipboardList className="w-16 h-16 text-coolslate/70 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-graphite mb-2">
                {selected.size > 0 ? 'No matching actions' : 'No actions yet'}
              </h3>
              <p className="text-coolslate">
                {selected.size > 0
                  ? 'Try clearing or adjusting the filter above.'
                  : 'Actions will appear here as users sign in, link files, edit fields, and more.'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-softgrey">
              {data.entries.map(entry => {
                const cfg = configFor(entry.action);
                return (
                  <div key={entry.id} className="py-5 first:pt-0 last:pb-0">
                    <div className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg}`}>
                        <cfg.Icon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="font-semibold text-graphite">{cfg.label}</h4>
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-softgrey/50 text-coolslate">
                            {entry.user_name}
                          </span>
                        </div>
                        <ActionDetail entry={entry} />
                        <div className="flex items-center gap-4 text-xs text-coolslate mt-2">
                          <span>{formatDateTime(entry.created_at)}</span>
                          {entry.section_id && <span>{entry.section_name || entry.section_id}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
